import React from 'react';
import {
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ResponsiveContainer,
  Legend,
  Tooltip,
} from 'recharts';
import { AnalyticsAggregation } from '../../services/analyticsApi';

interface ComparisonRadarChartProps {
  data: AnalyticsAggregation[];
  title: string;
  height?: number;
}

const ComparisonRadarChart: React.FC<ComparisonRadarChartProps> = ({
  data,
  title,
  height = 400,
}) => {
  // Transform data for radar chart - normalize values for better visualization
  const maxValue = Math.max(...data.map((d) => Math.max(d.pmshriCount, d.nonPmshriCount)));
  
  const chartData = data.map((item) => ({
    category: item.category.length > 12 ? item.category.substring(0, 12) + '...' : item.category,
    fullName: item.category,
    'PM-SHRI': item.pmshriCount,
    'Non PM-SHRI': item.nonPmshriCount,
    pmshriNormalized: maxValue > 0 ? (item.pmshriCount / maxValue) * 100 : 0,
    nonPmshriNormalized: maxValue > 0 ? (item.nonPmshriCount / maxValue) * 100 : 0,
  }));

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const item = payload[0].payload;
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-semibold text-gray-800 mb-2">{item.fullName}</p>
          <p className="text-green-600">
            <span className="inline-block w-2 h-2 rounded-full bg-green-500 mr-2"></span>
            PM-SHRI: {item['PM-SHRI'].toLocaleString()}
          </p>
          <p className="text-blue-600">
            <span className="inline-block w-2 h-2 rounded-full bg-blue-500 mr-2"></span>
            Non PM-SHRI: {item['Non PM-SHRI'].toLocaleString()}
          </p>
        </div>
      );
    }
    return null;
  };

  if (!data || data.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">{title}</h3>
        <div className="flex items-center justify-center h-64 text-gray-400">
          No data available
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">{title}</h3>
      <ResponsiveContainer width="100%" height={height}>
        <RadarChart data={chartData} margin={{ top: 20, right: 30, bottom: 20, left: 30 }}>
          <PolarGrid stroke="#e5e7eb" />
          {/* @ts-ignore - Recharts type compatibility issue */}
          <PolarAngleAxis
            dataKey="category"
            tick={{ fontSize: 11, fill: '#666' }}
          />
          {/* @ts-ignore - Recharts type compatibility issue */}
          <PolarRadiusAxis
            angle={30}
            domain={[0, 'auto']}
            tick={{ fontSize: 10, fill: '#999' }}
          />
          <Radar
            name="PM-SHRI"
            dataKey="PM-SHRI"
            stroke="#22c55e"
            fill="#22c55e"
            fillOpacity={0.3}
            strokeWidth={2}
          />
          <Radar
            name="Non PM-SHRI"
            dataKey="Non PM-SHRI"
            stroke="#3b82f6"
            fill="#3b82f6"
            fillOpacity={0.3}
            strokeWidth={2}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend wrapperStyle={{ paddingTop: 20 }} />
        </RadarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default ComparisonRadarChart;
